import { useNavigate } from '@tanstack/react-router'
import { useCommandPalette } from 'features/commandPalette/contexts/CommandPaletteContext'
import type { CommandPaletteTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { SelectedTab } from 'features/navbar/types'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import type { NavigateActiveTabOptions } from 'features/workspaceTabs/utils/targetTabs'
import { useCallback } from 'react'

export interface OpenCommandPaletteTargetOptions {
  inNewTab: boolean
}

const toTabOptions = (
  target: CommandPaletteTarget
): NavigateActiveTabOptions => {
  switch (target.kind) {
    case 'document':
      return {
        kind: 'document',
        documentId: target.documentId,
        label: target.label,
      }
    case 'settings':
      return {
        kind: 'destination',
        destinationKey: target.destinationKey,
        label: target.label,
        href: target.href,
      }
    case 'model':
    case 'entity': {
      const destination =
        target.kind === 'model' ? SelectedTab.Assets : target.destination
      return {
        kind: 'destination',
        destinationKey: destination,
        workspaceDestination: destination,
        resourceKey: target.id,
        label: target.label,
        href: target.href,
      }
    }
    case 'page':
      return {
        kind: 'destination',
        destinationKey: target.destination,
        workspaceDestination: target.destination,
        label: target.label,
        href: target.href,
      }
  }
}

/**
 * Opens a target in the current workspace tab, or in a new one with
 * `inNewTab`. Without the tab bar it plainly navigates. A tab provider
 * mounted only to host chats counts as no tab bar: its tabs are never shown,
 * so the palette must not write to them.
 */
export function useOpenCommandPaletteTarget(): (
  target: CommandPaletteTarget,
  options: OpenCommandPaletteTargetOptions
) => void {
  const { siteId, orgId } = useCommandPalette()
  const { workspaceTabs } = useNavbarConfig({ orgId: orgId ?? '', siteId })
  const actions = useOptionalWorkspaceTabsActions()
  const tabActions = workspaceTabs ? actions : null
  const navigate = useNavigate()

  return useCallback(
    (
      target: CommandPaletteTarget,
      { inNewTab }: OpenCommandPaletteTargetOptions
    ) => {
      if (!tabActions) {
        void navigate({ href: target.href })
        return
      }
      const options = toTabOptions(target)
      if (inNewTab) tabActions.openNewTab(options)
      else tabActions.navigateActiveTab(options)
    },
    [navigate, tabActions]
  )
}
